import { computeGrid, overallTotal, isPercentUnit, GRIDLESS_FREQUENCIES } from './targetGrid';
import type { Frequency, TargetAggregation } from '../types';

// Maps Django field names to Arabic display names
const FIELD_NAMES: Record<string, string> = {
  name: 'اسم المؤشر',
  description: 'الوصف',
  kpi_type: 'نوع المؤشر',
  unit: 'وحدة القياس',
  target_value: 'القيمة المستهدفة',
  worst_acceptable_value: 'القيمة القصوى المقبولة',
  frequency: 'دورية القياس',
  polarity: 'اتجاه المؤشر',
  weight: 'الوزن',
  classification: 'التصنيف',
  measurement_formula: 'معادلة القياس',
  measurement_start_date: 'تاريخ بداية القياس',
  data_source: 'مصدر البيانات',
  owner: 'مالك المؤشر',
  source_type: 'نوع المصدر',
  strategic_goal: 'الهدف الاستراتيجي',
  initiative: 'المبادرة',
  source_process: 'العملية',
  source_sub_process: 'العملية الفرعية',
  source_service: 'الخدمة',
  source_activity: 'النشاط',
  organizational_unit_goal: 'هدف الوحدة التنظيمية',
};

/**
 * Extracts a human-readable error message from Django REST Framework 400 responses.
 * DRF returns field-level errors as { field_name: ["error message"] }
 */
export function extractKpiErrorMessage(errorData: any): string {
  if (!errorData) return '';
  if (typeof errorData === 'string') return errorData;
  if (errorData.detail) return errorData.detail;
  if (errorData.non_field_errors) {
    return Array.isArray(errorData.non_field_errors)
      ? errorData.non_field_errors.join(', ')
      : errorData.non_field_errors;
  }

  // Field-level errors: { field: ["msg1"], field2: ["msg2"] }
  const fieldErrors: string[] = [];
  for (const [field, messages] of Object.entries(errorData)) {
    const label = FIELD_NAMES[field] || field;
    const msgs = Array.isArray(messages) ? messages.join(', ') : String(messages);
    fieldErrors.push(`${label}: ${msgs}`);
  }
  return fieldErrors.join(' | ');
}

/**
 * Required KPI fields config: { formField, label (Arabic) }
 * Used for frontend validation before submitting.
 * "startDate" variant is for screens using startDate, "measurementStartDate" for Process screens.
 */
interface KpiRequiredField {
  field: string;
  label: string;
}

const REQUIRED_FIELDS: KpiRequiredField[] = [
  { field: 'name', label: 'اسم المؤشر' },
  { field: 'description', label: 'وصف المؤشر' },
  { field: 'kpiType', label: 'نوع المؤشر' },
  { field: 'unit', label: 'وحدة القياس' },
  { field: 'targetValue', label: 'القيمة المستهدفة' },
  { field: 'frequency', label: 'دورية القياس' },
  { field: 'polarity', label: 'اتجاه المؤشر' },
  // NOTE: formula (معادلة القياس), unit description, and data source are
  // optional — backend allows blank, so they are intentionally NOT validated.
  // `targetValue` stays a presence-required field; every inline screen seeds it
  // to the '0.0' sentinel (the real target is authored via the annual grid).
];

/**
 * Derives the effective target for a grid-authored (new) KPI by mirroring
 * buildKpiTargetPayload: compute the annual grid from the kpi.target config and
 * aggregate it the same way the save payload does. Returns NaN when the grid
 * cannot be computed yet (missing frequency / start date / config) so callers
 * skip the >0 check until those required fields are filled.
 */
function gridTargetValue(kpi: any, dateField: string): number {
  const cfg = kpi.target;
  const startDate = kpi[dateField] || '';
  if (!cfg || !kpi.frequency || !startDate) return NaN;

  const cells = computeGrid({
    frequency: kpi.frequency as Frequency,
    pattern: cfg.targetPattern,
    unit: kpi.unit,
    start: startDate || null,
    end: cfg.verificationEndDate || null,
    stepPercent: parseFloat(cfg.targetStepPercent) || 0,
    stepAmount: parseFloat(cfg.targetStepAmount) || 0,
    inputs: cfg.inputs || {},
  });
  if (cells.length === 0) return NaN;

  const aggregation: TargetAggregation = isPercentUnit(kpi.unit) ? 'last' : cfg.targetAggregation;
  return overallTotal(cells.map((c) => c.value), kpi.unit, aggregation);
}

/**
 * Effective numeric target for a KPI form row — the single source of truth a
 * screen should validate against. Mirrors the save payload: the computed annual
 * grid total when the grid is authorable (start + end + frequency), else the
 * scalar `targetValue` (gridless frequencies, or before the grid dates are set).
 *
 * Screens MUST use this instead of `parseFloat(kpi.targetValue)`: the target is
 * authored through the grid, so the scalar is a stale '0' sentinel and reading
 * it directly produces a false "القيمة المستهدفة" error even when the grid is
 * filled. Returns NaN only when neither source is parseable.
 */
export function resolveKpiTarget(kpi: any, dateField: string = 'startDate'): number {
  const gridTarget = gridTargetValue(kpi, dateField);
  return !isNaN(gridTarget) ? gridTarget : parseFloat(kpi.targetValue);
}

/**
 * Validates required fields on all KPIs before save.
 * Returns null if valid, or a combined error message string listing
 * all missing fields across all KPIs (one line per KPI with issues).
 * dateField: the field name for measurement start date (varies per screen).
 */
export function validateKpiRequiredFields(
  kpis: any[],
  t: (key: string, opts?: any) => string,
  dateField: string = 'startDate',
): string | null {
  const allErrors: string[] = [];

  for (const kpi of kpis) {
    const name = kpi.name || kpi[REQUIRED_FIELDS[0].field];
    if (!name || !name.trim()) continue; // skip empty KPIs

    const missingFields: string[] = [];

    for (const { field, label } of REQUIRED_FIELDS) {
      const val = kpi[field];
      if (!val || (typeof val === 'string' && !val.trim())) {
        missingFields.push(label);
      }
    }

    // Target value checks. Every screen using this validator authors the target
    // through the annual grid (no editable scalar `targetValue` field is rendered)
    // for BOTH new and existing KPIs, so the effective target is the grid value
    // (same math as buildKpiTargetPayload — which the backend then mirrors onto
    // KPI.target_value). The stored scalar is stale here: an existing KPI saved
    // with an empty/zero grid loads targetValue '0', so reading it would reject a
    // freshly grid-entered target with "must be > 0" and block the save. Fall back
    // to the scalar only when the grid can't be computed (gridless frequency /
    // missing dates), where the scalar still drives the save.
    const isNegative = kpi.polarity === 'negative';

    const freq = kpi.frequency;
    const usesGrid = !!freq && !GRIDLESS_FREQUENCIES.has(freq as Frequency);
    const hasEnd = !!(kpi.target && kpi.target.verificationEndDate);
    const hasStart = !!(kpi[dateField] && String(kpi[dateField]).trim());

    const gridTarget = gridTargetValue(kpi, dateField);
    const targetNum = !isNaN(gridTarget) ? gridTarget : parseFloat(kpi.targetValue);

    if (usesGrid && isNaN(gridTarget) && hasStart && !hasEnd) {
      // The annual grid needs a start+end window to render any target cell; with
      // a start but no end date it silently yields a zero target. Surface the
      // real, fixable cause (the missing verification end date) instead of a
      // phantom "القيمة المستهدفة يجب أن تكون أكبر من 0" pointing at a field the
      // user can't see. Every OTHER uncomputable-grid case (missing start, or an
      // end-before-start that yields no periods) falls through to the target
      // check below, which keeps blocking the save rather than persisting a 0.
      missingFields.push('تاريخ التحقق (نهاية القياس) — مطلوب لإدخال القيم المستهدفة');
    } else if (!isNaN(targetNum)) {
      // Grid value (when computable) or the scalar for gridless frequencies.
      // Positive-direction KPIs still need a target > 0. Negative-polarity KPIs
      // may target 0 or a negative value (e.g. reduce a deficit), so no sign
      // check applies to them.
      if (!isNegative && targetNum <= 0) {
        missingFields.push('القيمة المستهدفة (يجب أن تكون أكبر من 0)');
      }
    }

    // worst_acceptable_value validation removed — the field is no longer
    // collected in any KPI form (deprecated UI).

    // Measurement start date is optional (backend KPISerializer:
    // measurement_start_date is required=False/allow_null=True; the model is
    // null=True). Only the verification END date stays mandatory — it drives
    // schedules + scoring — and is enforced in validateKpiTargetConfigs.

    if (missingFields.length > 0) {
      allErrors.push(`${name}: ${missingFields.join('، ')}`);
    }
  }

  if (allErrors.length === 0) return null;
  return allErrors.join('\n');
}

/**
 * Validates each KPI's annual-target CONFIG (end date, pattern, step values)
 * before submit. Mirrors the backend rules in KPISerializer.validate() and the
 * inline field checks in KpiAnnualTargetSection.validateKpiTargetConfig — kept in
 * sync deliberately so an invalid setup is caught client-side instead of
 * round-tripping to a swallowed 400. Complements validateKpiRequiredFields
 * (which only flags a missing end date for grid frequencies with a start set);
 * this also covers gridless frequencies and the variable-pattern step inputs.
 * Returns a combined message (one line per KPI with issues) or null when clean.
 * dateField: the KPI form's measurement-start field name (varies per screen).
 */
export function validateKpiTargetConfigs(
  kpis: any[],
  t: (key: string, opts?: any) => string,
  dateField: string = 'startDate',
): string | null {
  const allErrors: string[] = [];

  for (const kpi of kpis) {
    const name = kpi.name;
    if (!name || !name.trim()) continue; // skip empty KPIs
    const cfg = kpi.target;
    if (!cfg) continue;

    const start = kpi[dateField] || '';
    const msgs: string[] = [];

    const hasEnd = !!cfg.verificationEndDate;
    if (!hasEnd) {
      msgs.push(t('validation.endDateRequired'));
    } else if (start && cfg.verificationEndDate < start) {
      msgs.push(t('validation.endDateAfterStart'));
    }

    const pattern = cfg.targetPattern;
    if (pattern === 'pct_increase' || pattern === 'pct_decrease') {
      const sp = parseFloat(cfg.targetStepPercent);
      if (cfg.targetStepPercent === '' || isNaN(sp) || sp <= 0 || sp > 100) {
        msgs.push(t('validation.stepPercentInvalid'));
      }
    }
    if (pattern === 'num_increase' || pattern === 'num_decrease') {
      const sa = parseFloat(cfg.targetStepAmount);
      if (cfg.targetStepAmount === '' || isNaN(sa) || sa <= 0) {
        msgs.push(t('validation.stepAmountInvalid'));
      }
    }

    if (msgs.length > 0) allErrors.push(`${name}: ${msgs.join('، ')}`);
  }

  return allErrors.length === 0 ? null : allErrors.join('\n');
}

/**
 * Validates that KPI measurement_start_date does not exceed the strategic goal's target_completion_date.
 * Returns null if valid, or an error message string.
 */
export function validateKpiMeasurementDate(
  kpis: any[],
  goalTargetDate: string,
  dateField: string = 'startDate',
): string | null {
  if (!goalTargetDate) return null;

  const errors: string[] = [];
  for (const kpi of kpis) {
    const name = kpi.name;
    if (!name || !name.trim()) continue;

    const dateVal = kpi[dateField];
    if (dateVal && dateVal > goalTargetDate) {
      errors.push(
        `${name}: تاريخ بداية القياس (${dateVal}) يجب ألا يتجاوز تاريخ الإنجاز المستهدف للهدف (${goalTargetDate})`
      );
    }
  }

  if (errors.length === 0) return null;
  return errors.join('\n');
}
